import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import EvaluationNavigation from '../../components/EvaluationNavigation';
import MySpinner from '../../components/MySpinner';
import theme from '../../theme/theme';
import { getAcademicYearsAsync, getMyAnnualEvaluationAsync } from '../../services/annualEvaluationService';
import '../../assets/styles/annual-evaluation.css';

const ERROR_MAP = {
  'Academic year not found.': 'السنة الدراسية غير موجودة',
  'Cannot access a future academic year.': 'لا يمكن عرض سنة دراسية لم تبدأ بعد',
};

function mapErrorMessage(msg) {
  return ERROR_MAP[msg] || msg;
}

function fmt(n) {
  if (n === null || n === undefined) return null;
  return Math.ceil(n);
}

function getCurrentAcademicYearLabel() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return month >= 8 ? `${year}/${year + 1}` : `${year - 1}/${year}`;
}

function PositionCard({ position }) {
  const { isEvaluationPeriodEnded, isFinalized } = position._phase;
  const phase = !isEvaluationPeriodEnded ? 1 : !isFinalized ? 2 : 3;

  return (
    <div className="ae-position-card mb-4">
      <div className="ae-card-header">{position.positionName}</div>

      {phase === 1 && (
        <div className="ae-banner-warning">سوف تظهر نتائج تقييم المدير بعد انتهاء فترة التقييم</div>
      )}
      {phase === 2 && (
        <div className="ae-banner-info">جارٍ احتساب النتيجة النهائية من قبل ادارة النظام</div>
      )}

      <div className="ae-items-table mt-2">
        <table>
          <thead>
            <tr>
              <th>البند</th>
              <th>الإيداعات</th>
              <th>الوزن %</th>
              <th>التقدم</th>
              <th>الدرجة المرجحة</th>
            </tr>
          </thead>
          <tbody>
            {position.items.map((item) => (
              <tr key={item.itemId}>
                <td>{item.itemName}</td>
                <td>{item.actualDeposits} / {item.targetDeposits}</td>
                <td>{item.weight}</td>
                <td>
                  <div className="ae-progress-bar-track">
                    <div
                      className="ae-progress-bar-fill"
                      style={{ width: `${item.itemScore * 100}%` }}
                    />
                  </div>
                </td>
                <td>{fmt(item.weightedScore)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ae-score-row">
        <span>
          {phase === 3
            ? `الإيداعات (${position.dgWeight}%)`
            : 'درجة الإيداعات'}
        </span>
        <span className="ae-score-value">{fmt(position.depositGoalScore)} / 100</span>
      </div>

      {phase !== 1 && (
        <div className="ae-score-row">
          <span>
            {phase === 3
              ? `تقييم المشرف (${position.evWeight}%)`
              : 'تقييم المشرف'}
          </span>
          <span className="ae-score-value">
            {position.superiorEvaluationScore !== null
              ? `${fmt(position.superiorEvaluationScore)} / 100`
              : 'لم يتم تقديم التقييم'}
          </span>
        </div>
      )}

      {phase === 3 && position.finalScore !== null && (
        <div className="ae-final-score-badge">
          <span className="ae-badge-label">النتيجة النهائية</span>
          <span className="ae-badge-value">{fmt(position.finalScore)}</span>
        </div>
      )}
    </div>
  );
}

export default function AnnualEvaluationPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [evaluationData, setEvaluationData] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingScores, setLoadingScores] = useState(false);
  const [scoreError, setScoreError] = useState(null);

  const fetchScores = async (yearId) => {
    setEvaluationData(null);
    setScoreError(null);
    setLoadingScores(true);
    try {
      const response = await getMyAnnualEvaluationAsync(yearId);
      if (response.isSuccess) {
        const result = response.result;
        const phase = { isEvaluationPeriodEnded: result.isEvaluationPeriodEnded, isFinalized: result.isFinalized };
        const positions = (result.positions || []).map((p) => ({ ...p, _phase: phase }));
        setEvaluationData(positions);
      } else {
        const msgs = (response.errorMessages || []).map(mapErrorMessage);
        setScoreError(msgs.join(' — ') || 'حدث خطأ أثناء جلب البيانات.');
      }
    } catch {
      setScoreError('حدث خطأ أثناء جلب البيانات.');
    } finally {
      setLoadingScores(false);
    }
  };

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const years = await getAcademicYearsAsync();
        const list = years || [];
        const currentLabel = getCurrentAcademicYearLabel();
        const currentIndex = list.findIndex((y) => y.year === currentLabel);
        const startIndex = currentIndex >= 0 ? currentIndex : 0;
        const filtered = list.slice(startIndex, startIndex + 4);
        setAcademicYears(filtered);
        const match = filtered.find((y) => y.year === currentLabel);
        if (match) {
          setSelectedYearId(String(match.academicYearId));
          fetchScores(match.academicYearId);
        }
      } catch {
        setAcademicYears([]);
      } finally {
        setLoadingYears(false);
      }
    };
    fetchYears();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (e) => {
    const yearId = e.target.value;
    setSelectedYearId(yearId);
    if (!yearId) {
      setEvaluationData(null);
      setScoreError(null);
      return;
    }
    fetchScores(yearId);
  };

  return (
    <Layout>
      <EvaluationNavigation />
      <div className="container py-4" dir="rtl">

        <div className="ae-page-header mb-4">
          <h3 className="fw-bold">نتائج التقييم السنوي</h3>
        </div>

        <div className="ae-year-selector mb-4">
          {loadingYears ? (
            <MySpinner color={theme.colors.primary} />
          ) : (
            <select
              className="form-select"
              value={selectedYearId}
              onChange={handleYearChange}
            >
              <option value="">— اختر السنة الدراسية —</option>
              {academicYears.map((y) => (
                <option key={y.academicYearId} value={y.academicYearId}>
                  {y.year}
                </option>
              ))}
            </select>
          )}
        </div>

        {loadingScores && <MySpinner color={theme.colors.primary} />}

        {scoreError && (
          <div className="alert alert-danger">{scoreError}</div>
        )}

        {evaluationData && evaluationData.length === 0 && (
          <div className="alert alert-info text-center">لا توجد بيانات لهذه السنة.</div>
        )}

        {evaluationData && evaluationData.map((position) => (
          <PositionCard key={position.employeePositionId} position={position} />
        ))}

      </div>
    </Layout>
  );
}
